/**
 * Metric selection and chart-view settings above the metrics chart.
 *
 * Maps DOM state to a `ChartSelection` and keeps the one-line metric explanation
 * in sync. Everything visual (highlighting) is Bootstrap's `btn-check` styling,
 * so the selected state lives in the radio group rather than in JS state.
 */
import type { DisplayMode, MetricName, XAxisMode } from './metric-series.ts';
import { METRIC_HINTS } from './metric-series.ts';
import type { ChartSelection } from './metrics-chart.ts';

/** "All" is the only span value without a numeric window. */
const SPANS: ReadonlyMap<string, number | null> = new Map([
  ['all', null],
  ['250', 250],
  ['500', 500],
  ['1000', 1000],
]);

export class MetricsControls {
  #root: HTMLElement;
  #hint: HTMLElement;

  constructor(root: HTMLElement, hint: HTMLElement, onChange: (selection: ChartSelection) => void) {
    this.#root = root;
    this.#hint = hint;

    this.#root.addEventListener('change', () => {
      this.#renderHint();
      onChange(this.selection);
    });

    this.#renderHint();
  }

  get selection(): ChartSelection {
    return {
      xAxis: this.#option<XAxisMode>('metric-x-axis', ['batch', 'epoch'], 'batch'),
      metric: this.#option<MetricName>(
        'metric-name',
        ['accuracy', 'precision', 'recall', 'f1'],
        'accuracy',
      ),
      display: this.#option<DisplayMode>('metric-display', ['raw', 'moving'], 'raw'),
      span: SPANS.get(this.#selectedValue('metric-span')) ?? null,
    };
  }

  #option<T extends string>(name: string, allowed: readonly T[], fallback: T): T {
    const value = this.#selectedValue(name);
    return allowed.find((candidate) => candidate === value) ?? fallback;
  }

  #selectedValue(name: string): string {
    const selected = this.#root.querySelector<HTMLInputElement>(`input[name="${name}"]:checked`);
    return selected?.value ?? '';
  }

  #renderHint(): void {
    this.#hint.textContent = METRIC_HINTS[this.selection.metric];
  }
}
